import React from 'react';
import { makeStyles, Text, Badge, tokens } from '@fluentui/react-components';
import { StatusLegendProps, STATUS_COLORS, STATUS_LABELS, SiteStatus } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    marginRight: '4px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: '2px solid transparent',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  legendItemActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `2px solid ${tokens.colorBrandStroke1}`,
    boxShadow: tokens.shadow4,
  },
  colorBox: {
    width: '16px',
    height: '16px',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  label: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    fontWeight: '500',
  },
  count: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
});

export const StatusLegend: React.FC<StatusLegendProps> = ({
  summary,
  activeFilter,
  onStatusFilter,
}) => {
  const styles = useStyles();

  const legendItems = [
    {
      status: SiteStatus.COMPLETE,
      color: STATUS_COLORS.complete,
      label: STATUS_LABELS[SiteStatus.COMPLETE],
      count: summary.green_sites,
    },
    {
      status: SiteStatus.PARTIAL,
      color: STATUS_COLORS.partial,
      label: STATUS_LABELS[SiteStatus.PARTIAL],
      count: summary.yellow_sites,
    },
    {
      status: SiteStatus.NONE,
      color: STATUS_COLORS.none,
      label: STATUS_LABELS[SiteStatus.NONE],
      count: summary.red_sites,
    },
    {
      status: SiteStatus.NO_ASSIGNMENT,
      color: STATUS_COLORS.no_assignment,
      label: STATUS_LABELS[SiteStatus.NO_ASSIGNMENT],
      count: summary.grey_sites,
    },
  ];

  return (
    <div className={styles.container}>
      <Text className={styles.legendTitle}>Filter:</Text>
      {legendItems.map(item => (
        <div
          key={item.status}
          className={`${styles.legendItem} ${activeFilter === item.status ? styles.legendItemActive : ''}`}
          onClick={() => onStatusFilter(item.status)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onStatusFilter(item.status)}
        >
          <div className={styles.colorBox} style={{ backgroundColor: item.color }} />
          <Text className={styles.label}>{item.label}:</Text>
          <Badge appearance="outline" className={styles.count}>
            {item.count}
          </Badge>
        </div>
      ))}
      <div
        className={`${styles.legendItem} ${activeFilter === 'all' ? styles.legendItemActive : ''}`}
        onClick={() => onStatusFilter('all')}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onStatusFilter('all')}
      >
        <Text className={styles.label}>Total:</Text>
        <Badge appearance="filled" color="brand" className={styles.count}>
          {summary.total_sites}
        </Badge>
      </div>
    </div>
  );
};
